/**
 * CetusEd Community Portal — Parent Profile
 *
 * COMPLIANCE: FERPA §1232g(a)(1) — Parents have the right to access and
 *   request amendment of their child's education records. The profile
 *   displays the parent's linked students and contact information.
 *
 *   IDEA §300.322 — Schools must ensure parent contact information is
 *   current for meeting notifications. This profile page allows parents
 *   to update their contact information.
 */

import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useProfile, useUpdateProfile } from '../../api/hooks';
import { Button } from '../../ui/components/Button';
import { Card } from '../../ui/components/Card';
import { LoadingSpinner } from '../../ui/components/LoadingSpinner';

export const Profile: React.FC = () => {
  const { data: profile, isLoading, error } = useProfile();
  const updateProfile = useUpdateProfile();

  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  const handleStartEdit = useCallback(() => {
    if (profile) {
      setName(profile.name);
      setPhone(profile.phone || '');
    }
    setIsEditing(true);
  }, [profile]);

  const handleSave = useCallback(() => {
    // mutate() routes failures into updateProfile.isError, which is
    // rendered in the form — no swallowed rejection, no invisible failure.
    updateProfile.mutate({ name, phone }, { onSuccess: () => setIsEditing(false) });
  }, [name, phone, updateProfile]);

  if (isLoading) return <LoadingSpinner />;

  if (error || !profile) {
    return (
      <div className="error-page">
        <h1>Profile Not Available</h1>
        <p>Your profile could not be loaded. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <h1>My Profile</h1>

      <Card title="Contact Information">
        {isEditing ? (
          <div className="profile-edit-form">
            <div className="form-field">
              <label htmlFor="profile-name">Name</label>
              <input
                id="profile-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="profile-email">Email</label>
              <input
                id="profile-email"
                type="email"
                value={profile.email}
                disabled
              />
              <p className="field-hint">Email cannot be changed. Contact the school to update your email.</p>
            </div>
            <div className="form-field">
              <label htmlFor="profile-phone">Phone</label>
              <input
                id="profile-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
              />
            </div>
            {updateProfile.isError && (
              <div className="form-error" role="alert">
                Failed to save profile changes. Please try again.
              </div>
            )}
            <div className="form-actions">
              <Button onClick={handleSave} isLoading={updateProfile.isPending}>
                Save Changes
              </Button>
              <Button variant="ghost" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="profile-details">
            <p><strong>Name:</strong> {profile.name}</p>
            <p><strong>Email:</strong> {profile.email}</p>
            <p><strong>Phone:</strong> {profile.phone || 'Not provided'}</p>
            <Button onClick={handleStartEdit} variant="secondary">
              Edit Profile
            </Button>
          </div>
        )}
      </Card>

      <Card title="Linked Students">
        <div className="linked-students">
          {profile.linkedStudents.map((student) => (
            <div key={student.id} className="linked-student-row">
              <p><strong>{student.name}</strong></p>
              <p>{student.school} — Grade {student.grade}</p>
              <p className="relationship-badge">{student.relationship}</p>
            </div>
          ))}
        </div>
      </Card>

      <div className="profile-links">
        <Link to="/profile/notifications">
          <Button variant="ghost">Notification Preferences</Button>
        </Link>
      </div>
    </div>
  );
};
